import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function Login() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [emailMode, setEmailMode] = useState("login"); // login | register
  const [loading, setLoading] = useState(false);
  const [em, setEm] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "" });

  const submitEmail = async () => {
    if (!em.email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (em.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (emailMode === "register") {
      if (!em.first_name.trim()) { toast.error("Enter your first name"); return; }
      if (em.phone.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    }
    setLoading(true);
    try {
      const url = emailMode === "register" ? "/auth/register" : "/auth/login";
      const body = emailMode === "register" ? em : { email: em.email, password: em.password };
      const { data } = await api.post(url, body);
      loginWithToken(data.token, data.user);
      toast.success(emailMode === "register" ? "Account created successfully!" : "Logged in successfully!");
      navigate(loc.state?.from || "/account");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white border border-rose-900/15 rounded-2xl p-8 fade-up">
        <Button onClick={googleLogin} variant="outline" className="w-full h-12 rounded-full border-rose-900/20 mb-4 gap-2 font-medium" data-testid="login-google-btn">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          Continue with Google
        </Button>
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px flex-1 bg-rose-900/10" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-rose-900/10" />
        </div>

        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-rose-950/10 flex items-center justify-center mx-auto"><Mail className="w-7 h-7 text-rose-950" /></div>
          <h1 className="font-serif text-2xl font-bold text-rose-950 mt-4">{emailMode === "register" ? "Create Account" : "Email Login"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{emailMode === "register" ? "Sign up with your details" : "Login with your email & password"}</p>
        </div>
        <div className="space-y-3">
          {emailMode === "register" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name</Label><Input value={em.first_name} onChange={(e) => setEm({ ...em, first_name: e.target.value })} className="mt-1.5 h-11" data-testid="email-first-name" /></div>
              <div><Label>Last Name</Label><Input value={em.last_name} onChange={(e) => setEm({ ...em, last_name: e.target.value })} className="mt-1.5 h-11" data-testid="email-last-name" /></div>
            </div>
          )}
          <div><Label>Email</Label><Input type="email" value={em.email} onChange={(e) => setEm({ ...em, email: e.target.value })} className="mt-1.5 h-11" data-testid="email-input" /></div>
          {emailMode === "register" && (
            <div>
              <Label>Mobile Number <span className="text-rose-600">*</span></Label>
              <Input value={em.phone} onChange={(e) => setEm({ ...em, phone: e.target.value })} placeholder="+91 98765 43210" className="mt-1.5 h-11" data-testid="email-phone" />
              <p className="text-[11px] text-muted-foreground mt-1">Order updates isi number par milengi</p>
            </div>
          )}
          <div><Label>Password</Label><Input type="password" value={em.password} onChange={(e) => setEm({ ...em, password: e.target.value })} className="mt-1.5 h-11" data-testid="email-password" /></div>
          <Button onClick={submitEmail} disabled={loading} className="w-full bg-rose-950 hover:bg-rose-900 h-12 rounded-full" data-testid="email-submit-btn">{loading ? "Please wait..." : emailMode === "register" ? "Create Account" : "Login"}</Button>
          <button onClick={() => setEmailMode(emailMode === "register" ? "login" : "register")} className="text-sm text-amber-700 mx-auto block" data-testid="email-toggle-mode">{emailMode === "register" ? "Already have an account? Login" : "New here? Create an account"}</button>
        </div>
      </div>
    </div>
  );
}
